import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';
import core, { App } from '@gin-betting/core';
import { createStructuredSelector } from 'reselect';

import { useMemoSelector } from 'hooks';
import { setLoadScriptStatus } from '../../actions';
import { ViewerMode } from '../../constants';
import { selectIsFullScreen, selectLoadScriptStatus } from '../../selectors';
import { initMatchTracker, loadMatchTracker } from '../../utils';

interface Input {
    betradarId: string | null;
    viewerMode: ViewerMode;
    isContainerCompact: boolean;
    isSmall: boolean;
}

interface Output {
    loadScriptStatus: App.FetchStatus;
    pathname: string;
}

const {
    utils: { isInnerPage },
} = core;

const selectProps = createStructuredSelector({
    loadScriptStatus: selectLoadScriptStatus,
    isFullscreen: selectIsFullScreen,
});

const useBetradar = ({
    betradarId,
    viewerMode,
    isContainerCompact,
    isSmall,
}: Input): Output => {
    const dispatch = useDispatch();
    const { pathname } = useLocation();

    const { isFullscreen, loadScriptStatus } = useMemoSelector(selectProps);

    useEffect(() => {
        if (window.SIR || viewerMode !== ViewerMode.MATCH_TRACKER) {
            return;
        }

        const script = loadMatchTracker();

        script.addEventListener('load', () => {
            dispatch(
                setLoadScriptStatus({ loadStatus: App.FetchStatus.LOADED })
            );
        });
    }, [viewerMode, dispatch]);

    useEffect(() => {
        if (
            loadScriptStatus !== App.FetchStatus.LOADED ||
            viewerMode !== ViewerMode.MATCH_TRACKER
        ) {
            return;
        }

        const isListPage = !isInnerPage(pathname);

        initMatchTracker({
            betradarId,
            layout: isListPage && !isFullscreen ? 'double' : 'single',
            hasScoreboard: isFullscreen || (isListPage && !isContainerCompact),
        });
    }, [
        betradarId,
        loadScriptStatus,
        pathname,
        isFullscreen,
        viewerMode,
        isContainerCompact,
        isSmall,
    ]);

    return { loadScriptStatus, pathname };
};

export default useBetradar;
